import { useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useUpload } from "../../hooks/useUpload";
import { useExplorerStore } from "../../store/explorerStore";
import { UploadModal } from "./UploadModal";
import type { UploadPlan, ResolvedUploadFile } from "@titancloud/shared";

interface DropZoneProps {
  children: ReactNode;
}

export function DropZone({ children }: DropZoneProps) {
  const { pathname } = useLocation();
  const uploadsDisabled = pathname.includes("/admin/users/");
  const [isDragging, setIsDragging] = useState(false);
  const [plan, setPlan] = useState<UploadPlan | null>(null);
  const { scanAndPlan, executeUpload } = useUpload();
  const { currentFolderId } = useExplorerStore();

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!uploadsDisabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploadsDisabled) return;
    if (e.dataTransfer.items.length === 0) return;
    const scannedPlan = await scanAndPlan(e.dataTransfer);
    if (scannedPlan.totalFiles > 0) setPlan(scannedPlan);
  };

  const handleConfirm = async (resolved: ResolvedUploadFile[]) => {
    setPlan(null);
    await executeUpload(resolved);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className="relative h-full w-full"
    >
      {isDragging && (
        <div className="pointer-events-none absolute inset-0 z-40 m-4 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-brand-500 bg-brand-950/80 backdrop-blur-sm">
          <svg className="mb-3 h-12 w-12 text-brand-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
          <p className="text-lg font-semibold text-brand-300">Drop to upload</p>
          <p className="text-sm text-brand-400">Junk folders will be filtered automatically</p>
        </div>
      )}
      {children}
      {plan && (
        <UploadModal
          plan={plan}
          folderId={currentFolderId}
          onConfirm={handleConfirm}
          onCancel={() => setPlan(null)}
        />
      )}
    </div>
  );
}
